import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "~/lib/utils"
import { Tip, useTooltipsEnabled } from "~/components/ui/tip"

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none cursor-pointer focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-muted hover:text-foreground dark:hover:bg-muted",
        // FRONT-1779: a quiet action that sits ON a card without competing with
        // it — the Apps tile's Open / Coding session / More-actions controls.
        // At rest it is only its label: no background, no border, no shadow,
        // plain body text. The tint appears on hover alone, which is what keeps
        // a row of these from reading as a wall of chips. The icon draws with
        // currentColor (phosphor), so label and glyph stay in step.
        //
        // Hover reveals a thin grey hairline, not a fill. A background tint was
        // tried twice and rejected both times: the surfaces this sits on are
        // themselves near-white (`bg-background`), so any pale fill — blue or
        // white — was invisible against them. A border owes nothing to what is
        // behind the button, which is why it is the reliable affordance here.
        // The rest border is already `transparent`, so the hairline only
        // changes colour on hover and the button never shifts by a pixel.
        //
        // --soft-action-border is theme-aware and INVERTS: dark grey on light,
        // light grey on dark, since a dark line would vanish into a dark card.
        // Text stays --foreground throughout; only the outline appears.
        //
        // Focus is deliberately a different signal from hover: the base class
        // above paints a 3px soft ring, switched off here for a 2px offset
        // outline in --soft-action-ring (blue), so keyboard focus never reads
        // as "the mouse is here". This is NOT a state colour: success and
        // destructive still own "good" and "bad".
        soft:
          "border border-transparent bg-transparent text-foreground shadow-none " +
          "hover:border-soft-action-border hover:bg-transparent hover:text-foreground " +
          "focus-visible:ring-0 focus-visible:border-transparent " +
          "focus-visible:[outline:2px_solid_var(--soft-action-ring)] focus-visible:[outline-offset:2px] " +
          "transition-all duration-150 ease-[ease]",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

/**
 * FRONT-1192: `tooltip` is the replacement for a native `title` attribute on a
 * button. Set it and the button wraps itself in the design-system tooltip; leave
 * it off and nothing changes. It is a prop rather than a `<Tip>` at every call
 * site purely so migrating the ~193 existing `title` attributes is a rename.
 *
 * `tooltip` is NOT an accessible name. An icon-only button still needs its own
 * `aria-label` — the tooltip is a hint, and a hint that only exists on hover is
 * no name at all for a screen reader or a keyboard user.
 */
function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  tooltip,
  tooltipSide,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    tooltip?: React.ReactNode
    tooltipSide?: React.ComponentProps<typeof Tip>["side"]
  }) {
  const Comp = asChild ? Slot.Root : "button"
  // FRONT-2267: `Tip` gates itself on the same switch, so the hint is handled
  // either way. This read exists only for the disabled-wrapper branch below —
  // with hints off there is nothing for that span to carry, and adding a
  // wrapper element the page does not need is a DOM change for no reason.
  const tooltipsEnabled = useTooltipsEnabled()

  const button = (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )

  // A disabled button receives no pointer events, so the trigger has to sit on
  // a wrapper or the tooltip never opens — which is exactly the case that most
  // needs one, since the hint usually explains WHY it is disabled.
  // ponytail: `inline-flex` keeps the wrapper out of the way in both flex and
  // inline contexts. If a specific layout fights it, use `Tip` directly there
  // rather than growing this into a positioning API.
  if (tooltip && props.disabled && tooltipsEnabled) {
    return (
      <Tip label={tooltip} side={tooltipSide}>
        <span className="inline-flex">{button}</span>
      </Tip>
    )
  }

  return (
    <Tip label={tooltip} side={tooltipSide}>
      {button}
    </Tip>
  )
}

export { Button, buttonVariants }
